<template>
  <div :class="['alert', metricName]">
    <div class="alert__title">
      {{ $t('amd:alerts_configuration_title', { metricName: name }) }}
    </div>
    <div class="alert__settings">
      <div class="alert__settings__type">
        <div class="alert__settings__type__label label">
          {{ $t('amd:alerts_configuration_type_label') }}
        </div>
        <le-select
          theme="theme-light"
          class="alert__settings__type__combo"
          type="dropdown"
          :items="conditionTypes"
          :placeholder="`${$t('amd:alerts_configuration_type_combo_placeholder')}`"
          v-model="selectedConditionType"
          @input="onConditionTypeChange"
        />
      </div>
      <div v-if="showThreshold" class="alert__settings__values">
        <div class="alert__settings__values__block">
          <div v-if="!conditionTypeIsRange" class="alert__settings__values__block__single">
             <div class="alert__settings__values__label label">
              {{ $t('amd:alerts_configuration_threshold_label') }}
            </div>
            <duration
              ref="singleValue"
              v-model="selectedSingleValue"
              class="alert__settings__values__block__single__value input"
              />
          </div>
          <div v-else class="alert__settings__values__block__range">
            <div class="alert__settings__values__block__range__from">
              <div class="alert__settings__values__block__range__from__label label">
                {{ $t('amd:alerts_configuration_threshold_label_from') }}
              </div>
              <duration
                ref="rangeFromValue"
                v-model="selectedRangeFromValue"
                class="alert__settings__values__block__range__from__input input"
              />
            </div>

            <div class="alert__settings__values__block__range__separator"></div>

            <div class="alert__settings__values__block__range__to">
              <div class="alert__settings__values__block__range__to__label label">
                {{ $t('amd:alerts_configuration_threshold_label_to') }}
              </div>
              <duration
                ref="rangeToValue"
                v-model="selectedRangeToValue"
                class="alert__settings__values__block__range__to__input input"
              />
            </div>
          </div>
        </div>
      </div>
      <le-button
        v-if="showThreshold"
        class="alert__settings__reset"
        variant="link"
        :label="$t('amd:alerts_configuration_threshold_clear')"
        @click="reset">
      </le-button>
    </div>
  </div>
</template>

<script>
  /* eslint-disable max-len, no-unused-expressions */
  import { i18n } from 'vue-infra';
  import { map, isUndefined, get, isNull, isNaN, some, has, isEmpty, each } from 'lodash';
  import { leSelect, leInputField, leButton } from 'lp-vue-components';
  import {
    ALERT_CONDITION_TYPES,
    DEFAULT_FIELD_MAX_LENGTH,
    DURATION_TIME_UNITS,
    DURATION_VALIDATION_TARGET,
  } from '@config/Consts';
  import Duration from '@components/common/duration';

  const TIME_SETTINGS_RANGE = {
    FROM: 0,
    TO: 999,
  };

  const MAP_DURATION_UNIT_TO_SECONDS = {
    [DURATION_TIME_UNITS.HOURS]: 3600,
    [DURATION_TIME_UNITS.MINUTES]: 60,
    [DURATION_TIME_UNITS.SECONDS]: 1,
  };

  export default {
    name: 'alert-duration',
    components: { leSelect, leInputField, leButton, Duration },
    props: {
      metricName: {
        type: String,
        required: true,
      },
      conditionType: {
        type: String,
        default: undefined,
      },
      values: {
        type: Object,
        default: undefined,
      },
      name: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        selectedConditionType: null,
        selectedSingleValue: null,
        selectedRangeFromValue: null,
        selectedRangeToValue: null,
        changeEventEmitted: false,
      };
    },
    computed: {
      conditionTypes() {
        return map(ALERT_CONDITION_TYPES, item => ({ id: item, value: item }));
      },
      showThreshold() {
        return !isNull(this.selectedConditionType);
      },
      conditionTypeIsRange() {
        return get(this.selectedConditionType, 'id') === ALERT_CONDITION_TYPES.BETWEEN;
      },
      maxLength() {
        const maxValidNumber = isUndefined(this.validation) ?
          undefined : Math.max(Math.abs(this.validation.from), Math.abs(this.validation.to));
        return isUndefined(maxValidNumber) ? DEFAULT_FIELD_MAX_LENGTH : maxValidNumber.toString().length;
      },
    },
    watch: {
      conditionType: {
        handler() {
          this.selectedConditionType = this.conditionType ?
            this.formatForDropdown(this.conditionType) : null;
        },
        immediate: true,
      },
      values: {
        handler() {
          if (this.values) {
            this.selectedSingleValue = get(this.values, 'singleValue');
            this.selectedRangeFromValue = get(this.values, 'rangeFromValue');
            this.selectedRangeToValue = get(this.values, 'rangeToValue');
          }
        },
        immediate: true,
      },
      selectedConditionType() {
        this.clearSelectedValues();
        this.clearValidationMessageFields();
      },
      selectedSingleValue() {
        this.onSingleValueChange(this.selectedSingleValue);
      },
      selectedRangeFromValue() {
        this.onRangeFromValueChange(this.selectedRangeFromValue);
      },
      selectedRangeToValue() {
        this.onRangeToValueChange(this.selectedRangeToValue);
      },
    },
    methods: {
      convertDurationToInt(durationObj) {
        const value = Number(durationObj.duration);
        const unit = durationObj.unit;
        return value * MAP_DURATION_UNIT_TO_SECONDS[unit];
      },
      hasMissingValues() {
        return !isNull(this.selectedConditionType) &&
          ((!this.conditionTypeIsRange && isEmpty(this.selectedSingleValue)) ||
          (this.conditionTypeIsRange && (isEmpty(this.selectedRangeFromValue) || isEmpty(this.selectedRangeToValue))));
      },
      validateNumeric(value) {
        if (isNull(value)) {
          return { isValid: false, message: i18n.t('amd:alerts_configuration_threshold_required_validation'), target: DURATION_VALIDATION_TARGET.VALUE };
        }
        const rangeFrom = TIME_SETTINGS_RANGE.FROM;
        const rangeTo = TIME_SETTINGS_RANGE.TO;
        const isValidType = !isNaN(Number(value));
        const isInRange = isValidType && Number(value) >= rangeFrom && Number(value) <= rangeTo;
        if (!isValidType || !isInRange) {
          return { isValid: false, message: i18n.t('amd:alerts_configuration_threshold_range_validation', { from: rangeFrom, to: rangeTo }), target: DURATION_VALIDATION_TARGET.VALUE };
        }
        return { isValid: true, message: '', target: DURATION_VALIDATION_TARGET.VALUE };
      },
      validateUnit(unit) {
        if (isNull(unit)) {
          return { isValid: false, message: i18n.t('amd:alerts_configuration_threshold_required_validation'), target: DURATION_VALIDATION_TARGET.UNIT };
        }
        return { isValid: true, message: '', target: DURATION_VALIDATION_TARGET.UNIT };
      },
      validateDuration(durationObj) {
        const valueValidation = this.validateNumeric(durationObj.duration);
        const unitValidation = this.validateUnit(durationObj.unit);
        return [valueValidation, unitValidation];
      },
      getGlobalErrorValidationObject(message) {
        return [
          { isValid: false, message, target: DURATION_VALIDATION_TARGET.VALUE },
          { isValid: false, message, target: DURATION_VALIDATION_TARGET.UNIT },
        ];
      },
      setErrorInControls(validationObj, controls) {
        each(controls, control => control && control.setError(validationObj));
      },
      validateControls() {
        let isValid = true;
        if (isNull(this.selectedConditionType)) {
          return isValid;
        }
        const hasMissingValues = this.hasMissingValues();
        const missingValueMessage = i18n.t('amd:alerts_configuration_threshold_required_validation');
        if (hasMissingValues) {
          const validationError = this.getGlobalErrorValidationObject(missingValueMessage);
          this.setErrorInControls(validationError, [this.$refs.singleValue, this.$refs.rangeFromValue, this.$refs.rangeToValue]);
          isValid = false;
        } else if (!this.conditionTypeIsRange) {
          const singleValueValidation = this.validateDuration(this.selectedSingleValue);
          this.setErrorInControls(singleValueValidation, [this.$refs.singleValue]);
          isValid = !some(singleValueValidation, item => !item.isValid);
        } else {
          const rangeFromValueValidation = this.validateDuration(this.selectedRangeFromValue);
          const rangeToValueValidation = this.validateDuration(this.selectedRangeToValue);
          const validationArr = [...rangeFromValueValidation, ...rangeToValueValidation];
          if (some(validationArr, item => !item.isValid)) {
            this.setErrorInControls(rangeFromValueValidation, [this.$refs.rangeFromValue, this.$refs.rangeToValue]);
            isValid = false;
          } else if (this.convertDurationToInt(this.selectedRangeToValue) <= this.convertDurationToInt(this.selectedRangeFromValue)) {
            const message = i18n.t('amd:alerts_configuration_threshold_range_validation_tooltip');
            const betweenValidationErrorObj = this.getGlobalErrorValidationObject(message);
            this.setErrorInControls(betweenValidationErrorObj, [this.$refs.rangeFromValue, this.$refs.rangeToValue]);
            isValid = false;
          }
        }
        return isValid;
      },
      formatForDropdown(value) {
        return { id: value, value };
      },
      clearSelectedValues() {
        this.selectedSingleValue = null;
        this.selectedRangeFromValue = null;
        this.selectedRangeToValue = null;
      },
      clearValidationMessageFields() {
        has(this.$refs, 'singleValue') && !isUndefined(this.$refs.singleValue) && this.$refs.singleValue.clearError();
        has(this.$refs, 'rangeFromValue') && !isUndefined(this.$refs.rangeFromValue) && this.$refs.rangeFromValue.clearError();
        has(this.$refs, 'rangeToValue') && !isUndefined(this.$refs.rangeToValue) && this.$refs.rangeToValue.clearError();
      },
      emitChanges(field, value) {
        if (!this.changeEventEmitted) {
          this.$emit('onChange', { metricName: this.metricName, field, value });
          this.changeEventEmitted = true;
        }
      },
      normalizeDuration(durationObj) {
        if (isNull(durationObj)) {
          return null;
        }
        return {
          duration: isEmpty(durationObj.duration) ? null : durationObj.duration,
          unit: isEmpty(durationObj.unit) ? null : durationObj.unit,
        };
      },
      onConditionTypeChange(conditionType) {
        this.emitChanges('conditionType', conditionType.value);
      },
      onSingleValueChange(value) {
        this.emitChanges('singleValue', value);
      },
      onRangeFromValueChange(value) {
        this.emitChanges('rangeFromValue', value);
      },
      onRangeToValueChange(value) {
        this.emitChanges('rangeToValue', value);
      },

      /* *** PUBLIC METHODS ***** */
      validate() {
        const isValid = this.validateControls();
        if (isValid) {
          this.clearValidationMessageFields();
        }
        const inputData = isValid ? {} : {
          metricName: this.metricName,
          conditionType: this.selectedConditionType,
          singleValue: this.selectedSingleValue,
          rangeFromValue: this.selectedRangeFromValue,
          rangeToValue: this.selectedRangeToValue,
        };
        return { ...inputData, isValid };
      },
      reset() {
        this.$emit('onClear', this.getAlertData());
        this.selectedConditionType = null;
        this.clearSelectedValues();
        this.clearValidationMessageFields();
      },
      getAlertData() {
        let result = {
          metricName: this.metricName,
        };
        if (!isNull(this.selectedConditionType)) {
          const values = this.conditionTypeIsRange ? { rangeFromValue: this.selectedRangeFromValue, rangeToValue: this.selectedRangeToValue } : { singleValue: this.selectedSingleValue };
          result = {
            metricName: this.metricName,
            conditionType: this.selectedConditionType.id,
            values,
          };
        }
        return result;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .alert {
    margin: 5px 0 10px 0;
    .label {
      @include alert-dialog-label();
    }
    &__title {
      font-weight: bold;
      margin-bottom: 10px;
    }
    &__settings {
      display: flex;
      align-items: center;
      &__type {
        &__combo {
          width: 168px;
        }
      }
      &__values {
        margin-left: 10px;
        &__block {
          &__range {
            display: flex;
            align-items: center;
            &__separator {
              width: 5px;
              height: 1px;
              margin: 16px 10px 0;
              @include themify {
                 background-color: getColor('mainFilterSeparator', 'amd');
              }
            }
          }
        }
      }
      &__reset {
        margin: 18px 0 0 10px;
      }
    }
  }
</style>
